import type { FC } from 'react'
import { useCallback, useMemo } from 'react'

import ConversationHeader from './ConversationHeader'
import ConversationThread from './ConversationThread'
import {
  useConversationExpanded,
  useConversationLoader,
  useConversationReplied,
} from './hooks'
import { Container } from './styles'
import type { ConversationProps } from './types'
import { formatMessageText } from './utils'

const Conversation: FC<ConversationProps> = (props) => {
  const {
    isLoading,
    messages,
    isReplied: isRepliedDesktop,
    isMobile,
    reviewKey = '',
    postId,
    fetchComment,
    isConversationsEnabled,
    collaborators,
    isReadOnly,
    status,
    isStatusChangesVisible,
    isExternalNotesVisible,
    isPostActivityVisible,
    username,
  } = props

  const { isExpanded, setIsExpanded, getCollapseProps, getToggleProps } =
    useConversationExpanded(status, isMobile)

  const currentPost = messages?.find((item) => item.postId === postId)
  const currentPostMessages = useMemo(
    () => currentPost?.messages || [],
    [currentPost?.messages]
  )

  const filteredMessages = useMemo(() => {
    return currentPostMessages.filter((message) => {
      const { variant, visibility } = message

      const isPostActivityVariant =
        variant === 'postTypeChange' || variant === 'postCreated'
      if (!isPostActivityVisible && isPostActivityVariant) {
        return false
      }

      const isExternalNote = variant === 'note' && visibility === 'EXTERNAL'
      if (!isExternalNotesVisible && isExternalNote) {
        return false
      }

      if (variant === 'statusChange' && !isStatusChangesVisible) {
        return false
      }

      return true
    })
  }, [
    currentPostMessages,
    isExternalNotesVisible,
    isStatusChangesVisible,
    isPostActivityVisible,
  ])

  const hasMessages = filteredMessages.length > 0
  const isReplied = useConversationReplied(
    isRepliedDesktop,
    status,
    currentPostMessages
  )

  useConversationLoader(
    !!isExpanded,
    isLoading,
    reviewKey,
    postId,
    fetchComment
  )

  const formatText = useCallback(
    (text: string, textOnly = false) =>
      formatMessageText(text, collaborators, textOnly),
    [collaborators]
  )

  const handleToggle = useCallback(() => {
    if (!isLoading) {
      setIsExpanded(!isExpanded)
    }
  }, [isLoading, isExpanded, setIsExpanded])

  if (!isMobile && isLoading) return null

  if (filteredMessages.length === 0) return <></>

  return (
    <Container
      data-testid="conversation-container"
      isMobile={isMobile}
      isConversationsEnabled={isConversationsEnabled}
      onClick={handleToggle}
    >
      {isMobile && (
        <ConversationHeader
          isExpanded={!!isExpanded}
          isMobile={isMobile || false}
          isReadOnly={isReadOnly}
          hasMessages={hasMessages}
          isConversationsEnabled={isConversationsEnabled}
          onToggle={handleToggle}
          {...(isMobile ? getToggleProps() : {})}
        />
      )}

      <div {...(isMobile ? { ...getCollapseProps() } : {})}>
        <div>
          {(hasMessages || isReplied) && isConversationsEnabled && (
            <ConversationThread
              messages={filteredMessages}
              formatText={formatText}
              isReplied={isReplied}
              isMobile={isMobile || false}
              username={username}
            />
          )}
        </div>
      </div>
    </Container>
  )
}

export default Conversation
